<template>
  <div class="flex h-full flex-col gap-2">
    <div
      v-if="!original?.item"
      class="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center text-gray-500"
    >
      <FontAwesomeIcon :icon="['fal', 'circle-check']" size="lg" />
      <p class="text-sm font-semibold">
        {{ original?.item_name ?? original?.wiki_name }}
      </p>
      <p class="text-xs">{{ $t('global.no_stock_to_manage') }}</p>
    </div>
    <div
      v-if="original?.item"
      class="flex shrink-0 flex-col gap-1 rounded-lg border bg-slate-50 px-2 py-1.5"
    >
      <div class="flex flex-row items-center gap-2">
        <div class="min-w-0 flex-1">
          <h3 class="truncate text-sm font-bold">
            {{ original.wiki_name ?? original.item.item_name }}
          </h3>
          <p
            v-if="parentItemName"
            class="truncate text-xs text-gray-500 italic"
          >
            {{ $t('global.component_of', { name: parentItemName }) }}
          </p>
        </div>
        <span
          v-if="isAutomaticOrder"
          :data-tint="
            automaticOrderModeMeta?.bg_color ?? automaticOrderModeMeta?.color
          "
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary text-contrast-primary"
        >
          <FontAwesomeIcon
            :icon="['fal', automaticOrderModeMeta?.icon ?? 'truck-clock']"
            size="xs"
          />
        </span>
        <span
          v-if="isManualOrder"
          :data-tint="
            manualOrderModeMeta?.bg_color ?? manualOrderModeMeta?.color
          "
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary text-contrast-primary"
        >
          <FontAwesomeIcon
            :icon="['fal', manualOrderModeMeta?.icon ?? 'triangle-exclamation']"
            size="xs"
          />
        </span>
        <span
          class="shrink-0 rounded px-2 py-0.5 text-sm font-bold"
          :class="[
            sumSelectionItemLocations !== totalNeeded
              ? 'bg-red-500 text-white'
              : 'bg-green-500 text-white'
          ]"
          :title="$t('global.assigned_over_needed_quantity')"
        >
          {{ sumSelectionItemLocations }} / {{ totalNeeded }}
        </span>
      </div>
      <div
        v-if="isAutomaticOrder"
        class="flex items-start gap-1.5 rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-900"
      >
        <FontAwesomeIcon
          :icon="['fal', 'circle-info']"
          class="mt-0.5 shrink-0"
        />
        <span>
          <strong class="font-bold">
            {{ $t('global.automatic_order_title') }}
          </strong>
          — {{ $t('global.automatic_order_hint') }}
        </span>
      </div>
      <div
        v-if="isManualOrder"
        class="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-900"
      >
        <FontAwesomeIcon
          :icon="['fal', 'triangle-exclamation']"
          class="mt-0.5 shrink-0"
        />
        <span>
          <strong class="font-bold">
            {{ $t('global.manual_order_title') }}
          </strong>
          — {{ $t('global.manual_order_hint') }}
        </span>
      </div>
    </div>
    <div
      v-if="localOriginal?.item?.stockable"
      class="grid shrink-0 auto-rows-fr grid-cols-3 gap-1.5"
    >
      <button
        v-for="(option, idx) in currentViewOptions"
        :key="option.value"
        type="button"
        class="flex flex-col items-start gap-0.5 rounded-lg border px-2 py-1.5 text-left transition-colors"
        :class="[
          currentView === option.value
            ? 'border-primary bg-primary-50'
            : 'bg-white',
          option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        ]"
        :disabled="option.disabled"
        @click="currentView = option.value"
      >
        <div class="flex w-full flex-row items-center gap-1.5">
          <span
            class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-bold"
            :class="
              currentView === option.value
                ? 'border-primary bg-primary text-contrast-primary'
                : 'border-gray-300 text-gray-500'
            "
          >
            {{ idx + 1 }}
          </span>
          <h4 class="min-w-0 flex-1 truncate text-xs font-semibold">
            {{ option.label }}
          </h4>
          <FontAwesomeIcon
            :icon="['fal', option.icon]"
            class="shrink-0 text-gray-400"
          />
        </div>
        <p v-if="option.sub_label" class="truncate text-xs text-gray-500">
          {{ option.sub_label }}
        </p>
        <p class="line-clamp-2 text-xs text-gray-600">
          {{ option.description }}
        </p>
      </button>
    </div>
    <div
      v-if="isManualOrder && countAvailableLocal < totalNeeded"
      class="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-900"
    >
      <FontAwesomeIcon :icon="['fal', 'circle-info']" class="mt-0.5 shrink-0" />
      <span>
        {{ $t('global.manual_order_insufficient_local_stock_hint') }}
      </span>
    </div>
    <div
      v-if="original?.item"
      class="flex flex-1 flex-col gap-2 overflow-y-auto"
    >
      <template v-if="original?.item?.stockable">
        <template v-if="currentView === 'local'">
          <template
            v-for="(funeralHome, idx_fh) in stockLocalLocationDossier"
            :key="idx_fh"
          >
            <div
              v-for="(site, idx_s) in funeralHome"
              :key="idx_s"
              class="flex flex-col gap-1"
            >
              <div class="flex flex-row gap-2">
                <h3 class="font-semibold">
                  {{
                    sites.find((st) => st.id === idx_s)?.['name'] ??
                    `# ${idx_s}`
                  }}
                </h3>
                <small>
                  (
                  {{
                    funeralHomes.find((fh) => fh.id === idx_fh)?.['name'] ??
                    `# ${idx_fh}`
                  }}
                  )
                </small>
              </div>
              <div class="grid grid-cols-2 gap-2 lg:grid-cols-3">
                <template v-for="(location, idx_l) in site" :key="idx_l">
                  <StockSelectInStock
                    :quantity="localOriginal.stock[idx_l].quantity"
                    :quantity-available="location"
                    :disable-add-button="localOriginal.order === 0"
                    :name="
                      locations.find((l) => l.id === idx_l)?.['name'] ??
                      `# ${idx_l}`
                    "
                    @update:quantity="
                      (value) => handleStockQuantityChange(idx_l, value)
                    "
                  />
                </template>
              </div>
            </div>
          </template>
        </template>
        <template v-else-if="currentView === 'remote'">
          <template
            v-for="(funeralHome, idx_fh) in stockRemoteLocationDossier"
            :key="idx_fh"
          >
            <div
              v-for="(site, idx_s) in funeralHome"
              :key="idx_s"
              class="flex flex-col gap-1"
            >
              <div class="flex flex-row gap-2">
                <h3 class="font-semibold">
                  {{
                    sites.find((st) => st.id === idx_s)?.['name'] ??
                    `# ${idx_s}`
                  }}
                </h3>
                <small>
                  (
                  {{
                    funeralHomes.find((fh) => fh.id === idx_fh)?.['name'] ??
                    `# ${idx_fh}`
                  }}
                  )
                </small>
              </div>
              <div class="grid grid-cols-2 gap-2 lg:grid-cols-3">
                <template v-for="(location, idx_l) in site" :key="idx_l">
                  <StockSelectInStock
                    :quantity="localOriginal.stock[idx_l].quantity"
                    :quantity-available="location"
                    :disable-add-button="localOriginal.order === 0"
                    :name="
                      locations.find((l) => l.id === idx_l)?.['name'] ??
                      `# ${idx_l}`
                    "
                    @update:quantity="
                      (value) => handleStockQuantityChange(idx_l, value)
                    "
                  />
                </template>
              </div>
            </div>
          </template>
        </template>
        <template v-else-if="currentView === 'order'">
          <template v-if="isAutomaticOrder">
            <div
              class="mb-1 flex items-center gap-2 text-sm font-bold text-gray-800"
            >
              <span
                class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-contrast-primary"
              >
                1
              </span>
              <span class="capitalize-first">
                {{ $t('global.select_supplier_step') }}
              </span>
            </div>
            <div class="w-1/2">
              <SelectGroupedPickerComponent
                v-model="localOriginal.item.supplier_id"
                :groups="supplierList"
                name="supplier_id"
                track-by="id"
                display-key="name"
                :label="$t('attributes.supplier')"
                :disabled="hasRealSupplierOrderAttached"
                required
              />
            </div>
            <p
              v-if="hasRealSupplierOrderAttached"
              class="mt-1 text-xs text-gray-500"
            >
              {{ $t('global.supplier_locked_by_order_hint') }}
            </p>
            <template v-if="localOriginal.item.supplier_id">
              <div
                class="mt-3 mb-1 flex items-center gap-2 text-sm font-bold text-gray-800"
              >
                <span
                  class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-contrast-primary"
                >
                  2
                </span>
                <span class="capitalize-first">
                  {{ $t('global.select_or_create_order_step') }}
                </span>
              </div>
              <p
                v-if="autoAttachedSiblingName"
                class="mb-1 rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-900"
              >
                {{
                  $t('global.auto_attached_to_sibling_order', {
                    name: autoAttachedSiblingName
                  })
                }}
              </p>
              <LoadingComponent v-if="loadingSupplierOrders" />
              <template v-else>
                <template v-if="newSupplierOrder">
                  <StockSelectNewOrder
                    v-if="newSupplierOrder"
                    v-model:place-model="deliveryPlaceModel"
                    v-model:delivery-to-event-id="
                      localOriginal.new_supplier_order.delivery.event_id
                    "
                    v-model:delivery-date-time="
                      localOriginal.new_supplier_order.delivery
                        .delivery_date_time
                    "
                    v-model:supplier-id="
                      localOriginal.new_supplier_order.recipient_id
                    "
                    v-model:new-orders="localNewOrders"
                    :supplier-with-orders="supplierWithOrders"
                    :local-original="localOriginal"
                    :events="localDossier?.events ?? []"
                    @change-view="handleQuitNewOrderView"
                    @choose-suplier-order="handleButtonSelectExistingOrder"
                  />
                </template>
                <template
                  v-else-if="
                    !localOriginal.item.has_supplier &&
                    localOriginal.item.supplier_id
                  "
                >
                  <p
                    v-if="supplierOrdersActive?.length"
                    class="text-xs font-semibold text-gray-700 uppercase"
                  >
                    {{ $t('global.available_supplier_orders') }}
                  </p>
                  <p v-else class="text-xs text-gray-500">
                    {{ $t('global.no_draft_supplier_order') }}
                  </p>
                  <div
                    class="mt-2 grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
                  >
                    <StockSelectSupplierOrder
                      v-for="supplier_order in supplierOrdersActive"
                      :key="supplier_order.id"
                      :supplier-order="supplier_order"
                      :selected="
                        !newSupplierOrder &&
                        localOriginal.supplier_order_id === supplier_order.id
                      "
                      @click="
                        () => handleButtonSelectExistingOrder(supplier_order.id)
                      "
                    />
                    <button
                      type="button"
                      class="flex cursor-pointer flex-col items-center justify-center gap-1 squircle-lg rounded p-3 text-sm font-semibold transition-colors"
                      :class="[
                        newSupplierOrder
                          ? 'border border-primary bg-primary-50 text-primary'
                          : 'border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
                      ]"
                      @click="handleButtonNewOrder"
                    >
                      <FontAwesomeIcon
                        :icon="['fal', 'circle-plus']"
                        size="lg"
                      />
                      {{ $t('global.new_supplier_order') }}
                    </button>
                  </div>
                </template>
                <div v-else-if="localOriginal.item.has_supplier">
                  <p
                    v-if="effectiveSupplierOrders?.length"
                    class="text-xs font-semibold text-gray-700 uppercase"
                  >
                    {{ $t('global.available_supplier_orders') }}
                  </p>
                  <p v-else class="text-xs text-gray-500">
                    {{ $t('global.no_draft_supplier_order') }}
                  </p>
                  <div
                    class="mt-2 grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
                  >
                    <StockSelectSupplierOrder
                      v-for="supplier_order in effectiveSupplierOrders"
                      :key="supplier_order.id"
                      :supplier-order="supplier_order"
                      :selected="
                        !newSupplierOrder &&
                        localOriginal.supplier_order_id === supplier_order.id
                      "
                      @click="
                        () => handleButtonSelectExistingOrder(supplier_order.id)
                      "
                    />
                    <button
                      type="button"
                      class="flex cursor-pointer flex-col items-center justify-center gap-1 squircle-lg rounded p-3 text-sm font-semibold transition-colors"
                      :class="[
                        newSupplierOrder
                          ? 'border border-primary bg-primary-50 text-primary'
                          : 'border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
                      ]"
                      @click="handleButtonNewOrder"
                    >
                      <FontAwesomeIcon
                        :icon="['fal', 'circle-plus']"
                        size="lg"
                      />
                      {{ $t('global.new_supplier_order') }}
                    </button>
                  </div>
                </div>
              </template>
            </template>
          </template>
        </template>
      </template>
      <template v-else>
        <div
          class="mb-1 flex items-center gap-2 text-sm font-bold text-gray-800"
        >
          <span
            class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-contrast-primary"
          >
            1
          </span>
          <span class="capitalize-first">
            {{ $t('global.select_supplier_step') }}
          </span>
        </div>

        <div class="w-1/2">
          <SelectGroupedPickerComponent
            v-model="localOriginal.item.supplier_id"
            :groups="supplierList"
            name="supplier_id"
            track-by="id"
            display-key="name"
            :label="$t('attributes.supplier')"
            :disabled="hasRealSupplierOrderAttached"
            required
          />
        </div>
        <p
          v-if="hasRealSupplierOrderAttached"
          class="mt-1 text-xs text-gray-500"
        >
          {{ $t('global.supplier_locked_by_order_hint') }}
        </p>
        <div v-if="localOriginal.item.supplier_id">
          <div
            class="mt-3 mb-1 flex items-center gap-2 text-sm font-bold text-gray-800"
          >
            <span
              class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-contrast-primary"
            >
              2
            </span>
            <span class="capitalize-first">
              {{ $t('global.select_or_create_order_step') }}
            </span>
          </div>
          <p
            v-if="autoAttachedSiblingName"
            class="mb-1 rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-900"
          >
            {{
              $t('global.auto_attached_to_sibling_order', {
                name: autoAttachedSiblingName
              })
            }}
          </p>
          <LoadingComponent v-if="loadingSupplierOrders" />
          <template v-else>
            <StockSelectNewOrder
              v-if="newSupplierOrder"
              v-model:place-model="deliveryPlaceModel"
              v-model:delivery-to-event-id="
                localOriginal.new_supplier_order.delivery.event_id
              "
              v-model:delivery-date-time="
                localOriginal.new_supplier_order.delivery.delivery_date_time
              "
              v-model:supplier-id="
                localOriginal.new_supplier_order.recipient_id
              "
              v-model:new-orders="localNewOrders"
              :has-supplier="localOriginal.item.has_supplier"
              :supplier-with-orders="supplierWithOrders"
              :local-original="localOriginal"
              :events="localDossier?.events ?? []"
              @change-view="handleQuitNewOrderView"
              @choose-suplier-order="handleButtonSelectExistingOrder"
            />
            <div
              v-else-if="
                !localOriginal.item.has_supplier &&
                localOriginal.item.supplier_id
              "
            >
              <p
                v-if="supplierOrdersActive?.length"
                class="text-xs font-semibold text-gray-700 uppercase"
              >
                {{ $t('global.available_supplier_orders') }}
              </p>
              <p v-else class="text-xs text-gray-500">
                {{ $t('global.no_draft_supplier_order') }}
              </p>
              <div
                class="mt-2 grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
              >
                <StockSelectSupplierOrder
                  v-for="supplier_order in supplierOrdersActive"
                  :key="supplier_order.id"
                  :supplier-order="supplier_order"
                  :selected="
                    !newSupplierOrder &&
                    localOriginal.supplier_order_id === supplier_order.id
                  "
                  @click="
                    () => handleButtonSelectExistingOrder(supplier_order.id)
                  "
                />
                <button
                  type="button"
                  class="flex cursor-pointer flex-col items-center justify-center gap-1 squircle-lg rounded p-3 text-sm font-semibold transition-colors"
                  :class="[
                    newSupplierOrder
                      ? 'border border-primary bg-primary-50 text-primary'
                      : 'border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
                  ]"
                  @click="handleButtonNewOrder"
                >
                  <FontAwesomeIcon :icon="['fal', 'circle-plus']" size="lg" />
                  {{ $t('global.new_supplier_order') }}
                </button>
              </div>
            </div>
            <div v-else-if="localOriginal.item.has_supplier">
              <p
                v-if="effectiveSupplierOrders?.length"
                class="text-xs font-semibold text-gray-700 uppercase"
              >
                {{ $t('global.available_supplier_orders') }}
              </p>
              <p v-else class="text-xs text-gray-500">
                {{ $t('global.no_draft_supplier_order') }}
              </p>
              <div
                class="mt-2 grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
              >
                <StockSelectSupplierOrder
                  v-for="supplier_order in effectiveSupplierOrders"
                  :key="supplier_order.id"
                  :supplier-order="supplier_order"
                  :selected="
                    !newSupplierOrder &&
                    localOriginal.supplier_order_id === supplier_order.id
                  "
                  @click="
                    () => handleButtonSelectExistingOrder(supplier_order.id)
                  "
                />
                <button
                  v-if="original.item.has_supplier"
                  type="button"
                  class="flex cursor-pointer flex-col items-center justify-center gap-1 squircle-lg rounded p-3 text-sm font-semibold transition-colors"
                  :class="[
                    newSupplierOrder
                      ? 'border border-primary bg-primary-50 text-primary'
                      : 'border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
                  ]"
                  @click="handleButtonNewOrder"
                >
                  <FontAwesomeIcon :icon="['fal', 'circle-plus']" size="lg" />
                  {{ $t('global.new_supplier_order') }}
                </button>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
    <div
      v-if="myComponents.length"
      class="flex shrink-0 flex-col gap-1.5 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-2"
    >
      <div class="flex flex-row items-center gap-1.5">
        <FontAwesomeIcon
          :icon="['fal', 'link']"
          class="shrink-0 text-amber-600"
        />
        <h4 class="text-xs font-bold tracking-wide text-amber-800 uppercase">
          {{ $t('global.linked_items_count', { count: myComponents.length }) }}
        </h4>
      </div>
      <p class="text-xs text-amber-800">
        {{ $t('global.linked_items_hint') }}
      </p>
      <div
        v-for="component in myComponents"
        :key="component.id"
        class="overflow-hidden rounded-lg border bg-white"
      >
        <button
          type="button"
          class="flex w-full flex-row items-center gap-2 px-2 py-1.5 text-left"
          @click="toggleComponentExpanded(component.id)"
        >
          <FontAwesomeIcon
            :icon="[
              'fal',
              expandedComponentIds.includes(component.id)
                ? 'chevron-down'
                : 'chevron-right'
            ]"
            class="shrink-0 text-gray-500"
          />
          <span class="flex-1 truncate text-sm font-bold">
            {{ component.item?.item_name ?? component.item_name }}
          </span>
          <span class="shrink-0 text-xs text-gray-500">
            {{ $t('attributes.quantity') }} : {{ component.quantity }}
          </span>
        </button>
        <div
          v-show="expandedComponentIds.includes(component.id)"
          class="border-t p-2"
        >
          <AvailableItem
            :original="component"
            :new-orders="newOrders"
            :page="page"
            :all-originals="allOriginals"
            :supplier-with-orders="supplierWithOrders"
            :default-delivery="defaultDelivery"
            :default-supplier="defaultSupplier"
            :is-flowers-category="isFlowersCategory"
            :default-view="currentView"
            :parent-supplier-id="localOriginal.item?.supplier_id"
            :parent-supplier-order-id="effectiveSupplierOrderId"
            :is-nested="true"
            :ancestor-ids="[...ancestorIds, original.id]"
            @update:new-orders="$emit('update:new-orders', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { ItemCategoryGroup, OrderMode } from '@/assets/enums'
import { isClosedOrCancelled } from '@/assets/enums/supplierOrder/SupplierOrderStatus'
import { isItemDossierInFlowersCategory } from '@u/helpers/stockOriginalHelpers'
import StockSelectSupplierOrder from '@c/Stock/Component/component/StockSelectSupplierOrder.vue'
import StockSelectInStock from '@c/Stock/Component/component/StockSelectInStock.vue'
import StockSelectNewOrder from '@c/Stock/Component/component/StockSelectNewOrder.vue'
import SelectGroupedPickerComponent from '@c/BaseFormComponent/SelectGroupedPickerComponent.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'AvailableItem',
  components: {
    FontAwesomeIcon,
    LoadingComponent,
    SelectGroupedPickerComponent,
    StockSelectNewOrder,
    StockSelectInStock,
    StockSelectSupplierOrder
  },
  inject: [
    'draftOrdersBySupplier',
    'ensureDraftOrdersFetched',
    'addTempDraftOrder',
    'removeTempDraftOrder'
  ],
  props: {
    original: {
      type: Object,
      required: true
    },
    newOrders: {
      type: Array,
      required: false,
      default: () => []
    },
    supplierWithOrders: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultDelivery: {
      type: Object,
      required: false,
      default: () => null
    },
    defaultSupplier: {
      type: Object,
      required: false,
      default: () => null
    },
    page: {
      type: Number,
      required: true
    },
    isFlowersCategory: {
      type: Boolean,
      required: false,
      default: false
    },
    allOriginals: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultView: {
      type: String,
      required: false,
      default: null
    },
    parentSupplierId: {
      type: String,
      required: false,
      default: null
    },
    parentSupplierOrderId: {
      type: String,
      required: false,
      default: null
    },
    isNested: {
      type: Boolean,
      required: false,
      default: false
    },
    // Chaîne des id parents déjà rendus dans cette branche de récursion —
    // sert uniquement à détecter un cycle dans component_parent_id (données
    // catalogue mal configurées, ex. deux articles liés l'un à l'autre en
    // composant) : sans ça, myComponents() se re-déclenche à l'infini et
    // fait planter le navigateur (pile d'appels dépassée).
    ancestorIds: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['update:original', 'update:new-orders'],
  data() {
    return {
      newSupplierOrder: false,
      selectedSupplierOrderId: null,
      currentView: 'order',
      activeViewByPage: {},
      internalPage: 0,
      expandedComponentIds: [],
      autoAttachedSiblingName: null,
      hasDecidedInitialOrderView: false
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      invoice: 'invoicing/getInvoice',
      funeralHomes: 'metadata/getFuneralHomesMetadata',
      sites: 'metadata/getSitesMetadata',
      locations: 'metadata/getLocationsMetadata',
      suppliers: 'metadata/getSuppliersMetadata',
      orderModes: 'metadata/getMetadataItemOrderModes',
      item_categories_group_by_parent:
        'metadata/getMetadataItemCategoriesGroupByParent'
    }),
    // isFlowersCategory (prop) n'est fiable que lorsque l'appelant traite un
    // seul article/une seule catégorie à la fois (ex. onglet "Fleurs" du
    // dossier). Les appelants qui listent des articles de catégories mixtes
    // (nav du dossier, événements, automatisations, facture...) ne le
    // renseignent pas : on retombe alors sur la catégorie réelle de CET
    // article précis plutôt que de perdre le tri/le fournisseur par défaut.
    effectiveIsFlowersCategory() {
      return (
        this.isFlowersCategory ||
        isItemDossierInFlowersCategory(
          this.localOriginal?.item,
          this.item_categories_group_by_parent
        )
      )
    },
    isAutomaticOrder() {
      return this.localOriginal?.item?.order_mode === OrderMode.AUTOMATIC_ORDER
    },
    isManualOrder() {
      return this.localOriginal?.item?.order_mode === OrderMode.MANUAL_ORDER
    },
    automaticOrderModeMeta() {
      return (
        this.orderModes?.find(
          (mode) => mode.id === OrderMode.AUTOMATIC_ORDER
        ) ?? null
      )
    },
    manualOrderModeMeta() {
      return (
        this.orderModes?.find((mode) => mode.id === OrderMode.MANUAL_ORDER) ??
        null
      )
    },
    parentItemName() {
      if (!this.original?.component_parent_id) {
        return this.original?.parent_item_name ?? null
      }
      const parentEntry = this.allOriginals?.find(
        (entry) => entry?.id === this.original.component_parent_id
      )
      return (
        parentEntry?.item?.item_name ??
        parentEntry?.item_name ??
        this.original?.parent_item_name ??
        null
      )
    },
    myComponents() {
      if (!this.original?.id) return []
      return this.allOriginals.filter(
        (entry) =>
          entry?.component_parent_id === this.original.id &&
          entry.id !== this.original.id &&
          !this.ancestorIds.includes(entry.id)
      )
    },
    supplierList() {
      const groups = {}

      for (const item of this.suppliers) {
        const key = item.type ?? '__undefined__'
        const label = item.type_label ?? this.$t('attributes.undefined') // adapte la clé i18n
        if (!groups[key]) {
          groups[key] = {
            key,
            label,
            defaultKey: item.type_default_key,
            options: []
          }
        }
        groups[key].options.push(item)
      }

      let defined = Object.values(groups).filter(
        (g) => g.key !== '__undefined__'
      )
      if (this.effectiveIsFlowersCategory) {
        defined = defined.sort(
          (a, b) =>
            (b.defaultKey === ItemCategoryGroup.FLOWERS) -
            (a.defaultKey === ItemCategoryGroup.FLOWERS)
        )
      }
      const undefinedGroup = groups['__undefined__']
      const allGrouped = undefinedGroup ? [...defined, undefinedGroup] : defined
      return allGrouped
    },
    localDossier() {
      if (this.$route.name === 'invoice_info_route') {
        return this.invoice?.dossier
      } else {
        return this.dossier
      }
    },
    funeralHomeId() {
      if (this.dossier) {
        return this.dossier.funeral_home_id
      } else {
        return this.invoice?.funeral_home_id
      }
    },
    localOriginal: {
      get() {
        return this.original
      },
      set(value) {
        this.$emit('update:original', value)
      }
    },
    localNewOrders: {
      get() {
        return this.newOrders
      },
      set(value) {
        this.$emit('update:new-orders', value)
      }
    },
    deliveryPlaceModel: {
      get() {
        const delivery = this.localOriginal?.new_supplier_order?.delivery
        if (
          delivery?.place_model &&
          (delivery.place_model.full_name || delivery.place_model.name)
        ) {
          return delivery.place_model
        }
        return {
          place_id: delivery?.place_id ?? this.defaultDelivery?.id ?? null,
          place_type:
            delivery?.place_type ?? this.defaultDelivery?.kind ?? null,
          place_kind:
            delivery?.place_kind ?? this.defaultDelivery?.kind ?? null,
          full_name: this.defaultDelivery?.full_name ?? null
        }
      },
      set(value) {
        if (!value) return

        const placeKind = value.place_kind ?? value.kind ?? value.type ?? null
        const placeId = value.place_id ?? value.id ?? null

        this.localOriginal.new_supplier_order.delivery.place_name = [
          'SFU',
          'FUN'
        ].includes(value.type)
          ? value.full_name
          : (value.name?.[this.$i18n.locale] ?? value.name)
        this.localOriginal.new_supplier_order.delivery.place_type = placeKind
        this.localOriginal.new_supplier_order.delivery.place_kind = placeKind
        this.localOriginal.new_supplier_order.delivery.place_id = placeId
        this.localOriginal.new_supplier_order.delivery.place_model = value

        this.checkAndCreateTempOrder?.()
      }
    },
    ownSupplierOrders() {
      const supplier_t = this.localOriginal.item?.supplier_id
      if (!supplier_t) return []
      const fromSharedCache = this.draftOrdersBySupplier[supplier_t]
      if (Array.isArray(fromSharedCache)) {
        return fromSharedCache
      }
      return (
        this.supplierWithOrders?.find((supplier) => supplier.id === supplier_t)
          ?.supplier_orders ?? []
      )
    },
    supplierOrdersActive() {
      if (this.localOriginal.item?.has_supplier) {
        return this.ownSupplierOrders
      }
      const supplier_t = this.localOriginal.item?.supplier_id
      if (!supplier_t) return this.ownSupplierOrders
      // Commandes brouillon du fournisseur sélectionné uniquement : le cache
      // partagé contient les brouillons de tous les fournisseurs, il ne faut
      // pas les mélanger ici.
      const allTempOrders = Object.values(this.draftOrdersBySupplier)
        .filter((orders) => Array.isArray(orders))
        .flat()
        .filter(
          (order) =>
            order?.id?.toString().startsWith('temp-') &&
            order.recipient_id === supplier_t
        )
      const ownIds = new Set(this.ownSupplierOrders.map((order) => order.id))
      const otherTempOrders = allTempOrders.filter(
        (order) => !ownIds.has(order.id)
      )
      return [...this.ownSupplierOrders, ...otherTempOrders]
    },
    loadingSupplierOrders() {
      const supplier_t = this.localOriginal.item?.supplier_id
      return supplier_t
        ? this.draftOrdersBySupplier[supplier_t] === null
        : false
    },
    effectiveSupplierOrders() {
      // Le back ne renvoie plus `original[].supplier_orders` (retiré côté API,
      // BBSW-9257) : la seule source désormais est le cache partagé des
      // brouillons du fournisseur.
      return Array.isArray(this.ownSupplierOrders) ? this.ownSupplierOrders : []
    },
    effectiveSupplierOrderId() {
      if (this.newSupplierOrder) {
        return this.localOriginal.new_supplier_order?.id ?? null
      }
      return this.localOriginal.supplier_order_id ?? null
    },
    hasRealSupplierOrderAttached() {
      // `item.has_supplier` (back) ne reflète que si un supplier_id était déjà
      // renseigné au chargement — un snapshot figé, pas l'état courant du choix
      // de commande dans cet écran. Pour verrouiller le champ fournisseur une
      // fois un BDC réel réellement sélectionné/attaché (BBSW-9257), on se base
      // sur `supplier_order_id` lui-même, pas sur ce flag.
      const orderId = this.localOriginal.supplier_order_id
      if (!orderId || orderId.toString().startsWith('temp-')) return false
      const order = this.effectiveSupplierOrders?.find(
        (candidate) => candidate.id === orderId
      )
      return order ? !isClosedOrCancelled(order.status) : true
    },
    stockLocalLocationDossier() {
      const id = this.funeralHomeId
      if (this.localOriginal.item?.stocks?.[id]) {
        return { [id]: this.localOriginal.item.stocks[id] }
      }
      return {}
    },
    stockRemoteLocationDossier() {
      return Object.fromEntries(
        Object.entries(this.localOriginal.item?.stocks ?? {}).filter(
          ([key]) => key !== this.funeralHomeId
        )
      )
    },
    countAvailableLocal() {
      return this.sumStocks(this.stockLocalLocationDossier)
    },
    countAvailableRemote() {
      return this.sumStocks(this.stockRemoteLocationDossier)
    },
    countSelectedLocal() {
      return Object.values(this.localOriginal.stock ?? {})
        .filter((item) => item.funeral_home_id === this.funeralHomeId)
        .map((item) => item.quantity)
        .filter((n) => parseInt(n) === n)
        .reduce((a, b) => a + b, 0)
    },
    countSelectedRemote() {
      return Object.values(this.localOriginal.stock ?? {})
        .filter((item) => item.funeral_home_id !== this.funeralHomeId)
        .map((item) => item.quantity)
        .filter((n) => parseInt(n) === n)
        .reduce((a, b) => a + b, 0)
    },
    totalNeeded() {
      if (!this.original.item.stockable)
        return this.original.item.remaining_quantity
      return (
        this.original.item.remaining_quantity +
        this.original.item.item_quantity +
        this.original.item.already_allocate
      )
    },
    sumSelectionItemLocations() {
      if (!this.localOriginal.item.stockable) return this.localOriginal.quantity
      return (
        Object.values(this.localOriginal.stock)
          .map((item) => item.quantity)
          .filter((n) => parseInt(n) === n)
          .reduce((a, b) => a + b, 0) +
        this.localOriginal.order +
        this.localOriginal.item.already_allocate
      )
    },
    currentViewOptions() {
      return [
        {
          value: 'local',
          label: `${this.funeralHomes.find((funeralHome) => funeralHome.id === this.funeralHomeId)?.name ?? this.$t('global.unknown')} - ${this.countSelectedLocal} ${this.$t('global.selected_v')}`,
          sub_label: `${this.countAvailableLocal} ${this.$t('global.available_v')}`,
          description:
            this.countAvailableLocal > 0
              ? this.$t('global.local_stock_available_count', {
                  count: this.countAvailableLocal
                })
              : this.$t('global.local_stock_none_available'),
          icon: 'location-dot',
          disabled:
            this.countAvailableLocal === 0 ||
            (this.countSelectedLocal === 0 && this.localOriginal.order === 0)
        },
        {
          value: 'remote',
          label: `${this.$t('global.other')} - ${this.countSelectedRemote} ${this.$t('global.selected_v')}`,
          sub_label: `${this.countAvailableRemote} ${this.$t('global.available_v')}`,
          description:
            this.countAvailableRemote > 0
              ? this.$t('global.remote_stock_available_count', {
                  count: this.countAvailableRemote
                })
              : this.$t('global.remote_stock_none_available'),
          icon: 'map-location',
          disabled:
            this.countAvailableRemote === 0 ||
            (this.countSelectedRemote === 0 && this.localOriginal.order === 0)
        },
        {
          value: 'order',
          label: `${this.$t('global.order')} - ${this.localOriginal.order} ${this.$t('global.to_be_order')}`,
          sub_label: this.$t('global.quantity_to_order'),
          description:
            this.localOriginal.order > 0
              ? this.$t('global.missing_quantity_will_be_ordered', {
                  count: this.localOriginal.order
                })
              : this.$t('global.no_order_needed'),
          icon: 'basket-shopping',
          disabled: this.localOriginal.order <= 0,
          gate: this.isAutomaticOrder
        }
      ].filter(
        (item) =>
          !item.hasOwnProperty('gate') ||
          (item.gate !== false && item.gate !== undefined)
      )
    }
  },
  watch: {
    defaultView(newValue) {
      if (
        newValue &&
        this.currentViewOptions.some((option) => option.value === newValue)
      ) {
        this.currentView = newValue
      }
    },
    parentSupplierId(newValue) {
      // Même priorité que created() (BBSW-5386) : fournisseur du parent >
      // catalogue (has_supplier) > fournisseur par défaut du dossier
      // (fleurs uniquement). Si le parent a un fournisseur, il l'emporte
      // toujours ; sinon on ne touche pas à un fournisseur imposé par le
      // catalogue du composant.
      if (!this.isNested || !this.localOriginal?.item) return
      if (newValue) {
        this.localOriginal.item.supplier_id = newValue
      } else if (!this.localOriginal.item.has_supplier) {
        this.localOriginal.item.supplier_id = this.effectiveIsFlowersCategory
          ? (this.defaultSupplier?.id ?? null)
          : null
      }
    },
    parentSupplierOrderId() {
      this.syncOrderSelectionFromParent()
    },
    'original.stock': {
      handler(newValue) {
        this.calcNumToOrder()
      },
      deep: true
    },
    'localOriginal.item.supplier_id': {
      handler(newValue) {
        if (!this.isAutomaticOrder) return
        if (newValue) {
          const staleDraft =
            this.localOriginal.new_supplier_order?.id &&
            this.localOriginal.new_supplier_order?.recipient_id !== newValue
          if (staleDraft) {
            this.handleQuitNewOrderView()
          } else {
            // En attendant le résultat du chargement, on privilégie la vue
            // "commandes en cours" plutôt que de rester sur la vue de création,
            // héritée du moment où aucun fournisseur n'était encore choisi.
            this.newSupplierOrder = false
          }
          this.ensureDraftOrdersFetched(newValue)
          this.applyDraftOrdersDecision(this.supplierOrdersActive, {
            autoCreateIfEmpty: false
          })
        } else {
          this.newSupplierOrder = true
        }
      }
    },
    supplierOrdersActive(newValue) {
      // Ce watcher se redéclenche à chaque mutation du cache partagé, y
      // compris celles causées par nos propres actions (créer un brouillon,
      // le supprimer via "Retour"...). Il ne doit jamais décider seul de
      // créer une nouvelle commande : seule l'ouverture initiale
      // (mounted -> maybeAutoOpenNewOrderForDedicatedSupplier) le fait —
      // via son propre garde-fou hasDecidedInitialOrderView, pas celui-ci.
      this.applyDraftOrdersDecision(newValue, { autoCreateIfEmpty: false })
      // Le fetch initial (ensureDraftOrdersFetched, déclenché au created())
      // est asynchrone : ce watcher est le signal fiable de sa résolution
      // (le cache partagé passe de `null` à un tableau réel), donc c'est ici
      // qu'on retente la décision "créer un nouveau brouillon ?" une fois
      // qu'on sait vraiment si des commandes existantes existent déjà.
      this.maybeAutoOpenNewOrderForDedicatedSupplier()
    },
    'localOriginal.new_supplier_order.delivery.event_id': {
      handler(newValue) {
        this.checkAndCreateTempOrder()
      }
    },
    'localOriginal.new_supplier_order.delivery.delivery_date_time': {
      handler(newValue) {
        this.localOriginal.new_supplier_order.delivery.delivery_date_time =
          newValue
        this.localOriginal.new_supplier_order.delivery_date_time = newValue
        this.checkAndCreateTempOrder()
      }
    }
    // Le watch précédent sur `page` n'était plus jamais déclenché : le
    // parent (AvailableLocationStockModal.vue) met `page` dans le :key de
    // cette instance, donc un changement de page démonte/remonte le
    // composant au lieu de faire varier ce prop en place. Son effet — la
    // reprise d'un brouillon de commande déjà réel — est reproduit dans
    // created() (équivalent au remount).
  },
  created() {
    this.ensureStockEntriesForAllLocations()
    this.resetToDefaultView()
    // Reprise directe d'un brouillon de commande déjà réel (ou d'un
    // placeholder "temp-" en cours de partage entre composants) : sans ce
    // check, un article rouvert sur cette page bascule à tort sur la liste
    // des commandes existantes au lieu de continuer l'édition en cours.
    if (
      this.original.new_supplier_order?.id ||
      this.original.supplier_order_id?.toString().startsWith('temp-')
    ) {
      this.newSupplierOrder = true
    }
    if (this.isNested && this.localOriginal?.item) {
      // Priorité au fournisseur de l'article parent (ruban/carte commandés
      // chez le même fournisseur que la fleur à laquelle ils sont
      // rattachés). S'il n'en a pas, has_supplier : le catalogue impose déjà
      // un fournisseur pour ce composant précis (supplier_id renvoyé par le
      // back) — on n'y touche pas. Si ni l'un ni l'autre, on retombe sur le
      // fournisseur par défaut du dossier pour les fleurs — même logique de
      // repli que la branche non-imbriquée ci-dessous (BBSW-10592) — pour ne
      // pas devoir le resélectionner manuellement au moment de la commande
      // (BBSW-5386).
      if (this.parentSupplierId) {
        this.localOriginal.item.supplier_id = this.parentSupplierId
      } else if (!this.localOriginal.item.has_supplier) {
        this.localOriginal.item.supplier_id = this.effectiveIsFlowersCategory
          ? (this.defaultSupplier?.id ?? null)
          : null
      }
    } else if (
      this.defaultSupplier?.id &&
      this.effectiveIsFlowersCategory &&
      this.localOriginal?.item &&
      !this.localOriginal.item.supplier_id &&
      !this.localOriginal.item.has_supplier
    ) {
      // Fournisseur par défaut du dossier (ticket BBSW-10592) : ne préremplit
      // que si l'article traité est une fleur et n'a pas déjà de fournisseur
      // choisi/imposé, et reste modifiable ligne par ligne ensuite. Le back
      // renvoie toujours le fournisseur par défaut du dossier quel que soit
      // l'article ; c'est ici qu'on décide s'il s'applique.
      this.localOriginal.item.supplier_id = this.defaultSupplier.id
    }
    this.maybeAdoptSiblingOrder()
    const supplierId = this.localOriginal?.item?.supplier_id
    if (supplierId && this.isAutomaticOrder) {
      this.ensureDraftOrdersFetched(supplierId)
      // autoCreateIfEmpty: false — au moment du created(), le fetch async
      // ci-dessus n'a pas encore résolu, donc "aucune commande" ici ne veut
      // rien dire (il ne sait tout simplement pas encore). Le watcher
      // supplierOrdersActive refera cet appel une fois le cache partagé
      // résolu ; seul maybeAutoOpenNewOrderForDedicatedSupplier (qui, lui,
      // attend explicitement cette résolution) est autorisé à créer un
      // brouillon automatiquement (BBSW-9257).
      this.applyDraftOrdersDecision(this.supplierOrdersActive, {
        autoCreateIfEmpty: false
      })
    }
    this.calcNumToOrder()
    this.maybeAutoOpenNewOrderForDedicatedSupplier()
  },
  methods: {
    ensureStockEntriesForAllLocations() {
      // Le back n'envoie plus qu'une entrée par location dans `stock` pour
      // celles où l'article a réellement du stock (BBSW-9257) — les
      // locations d'une autre agence, à quantity 0 dans `stock` mais avec du
      // stock réel visible via `item.stocks` (vue "disponible ailleurs",
      // BBSW-10251), n'y figurent donc plus. Le template indexe directement
      // `localOriginal.stock[idx_l]` (v-model, pas de fallback possible
      // dans ce binding) pour CHAQUE location présente dans `item.stocks` :
      // on reconstruit ici les entrées manquantes à quantity 0, à partir de
      // données déjà connues du front, pour que cet index ne soit jamais
      // undefined.
      const stocksByFuneralHome = this.localOriginal?.item?.stocks
      if (!stocksByFuneralHome) return
      // Un tableau vide `[]` (PHP : tableau associatif vide envoyé par le
      // back quand l'article n'a de stock nulle part) est truthy en JS — le
      // garde `!this.localOriginal.stock` seul ne le détecte pas et laisse
      // `stock` en tableau. Les affectations par clé texte qui suivent
      // (`stock[locationId] = ...`) restent alors lisibles en mémoire
      // (Object.values/keys les voient) mais disparaissent silencieusement
      // à la sérialisation JSON de la requête (un tableau ne sérialise que
      // ses indices numériques) : la sélection de stock partait vide vers
      // le back, qui renvoyait une 422 ("quantités définies différentes des
      // quantités requises").
      if (
        !this.localOriginal.stock ||
        Array.isArray(this.localOriginal.stock)
      ) {
        this.localOriginal.stock = {}
      }
      Object.entries(stocksByFuneralHome).forEach(([funeralHomeId, sites]) => {
        Object.values(sites ?? {}).forEach((locationsQuantities) => {
          Object.keys(locationsQuantities ?? {}).forEach((locationId) => {
            if (!this.localOriginal.stock[locationId]) {
              this.localOriginal.stock[locationId] = {
                location_id: locationId,
                funeral_home_id: funeralHomeId,
                quantity: 0
              }
            }
          })
        })
      })
    },
    resetToDefaultView() {
      if (
        this.defaultView &&
        this.currentViewOptions.some(
          (option) => option.value === this.defaultView
        )
      ) {
        this.currentView = this.defaultView
        return
      }
      if (this.isAutomaticOrder) {
        this.currentView = 'order'
      } else if (this.isManualOrder) {
        this.currentView = 'remote'
      } else {
        this.currentView = 'local'
      }
    },
    maybeAdoptSiblingOrder() {
      if (this.isNested) return
      if (!this.isAutomaticOrder) return
      if (!this.localOriginal?.item) return
      if (
        this.localOriginal.supplier_order_id ||
        this.localOriginal.new_supplier_order?.id
      ) {
        return
      }
      if (!this.original?.component_parent_id) return

      const sibling = (this.allOriginals ?? []).find(
        (entry) =>
          entry !== this.original &&
          entry?.component_parent_id === this.original.component_parent_id &&
          (entry?.supplier_order_id || entry?.new_supplier_order?.id)
      )
      if (!sibling) return

      const siblingOrderId =
        sibling.supplier_order_id ?? sibling.new_supplier_order?.id
      const siblingSupplierId =
        sibling.item?.supplier_id ?? sibling.new_supplier_order?.recipient_id
      if (!siblingOrderId || !siblingSupplierId) return

      if (
        this.localOriginal.item.has_supplier &&
        this.localOriginal.item.supplier_id !== siblingSupplierId
      ) {
        // Fournisseur imposé différent de celui du frère : pas de rattachement automatique possible.
        return
      }

      this.localOriginal.item.supplier_id = siblingSupplierId
      this.localOriginal.delivery_place_id = null
      this.localOriginal.supplier_order_id = siblingOrderId
      if (sibling.new_supplier_order?.id === siblingOrderId) {
        // Le back attend le détail complet de la commande (livraison, destinataire, …)
        // sur chaque article qui la référence, pas seulement son id.
        const existingItemIds = Array.isArray(
          sibling.new_supplier_order.item_ids
        )
          ? sibling.new_supplier_order.item_ids
          : []
        const mergedItemIds = [
          ...new Set([...existingItemIds, this.localOriginal.item.item_id])
        ]
        // On répercute aussi la fusion sur la copie du frère : sinon sa
        // prochaine modification (ex. la livraison) relirait son item_ids
        // resté obsolète et écraserait le cache partagé en perdant ce
        // rattachement (BBSW-9257).
        sibling.new_supplier_order.item_ids = mergedItemIds
        this.localOriginal.new_supplier_order = {
          ...sibling.new_supplier_order,
          item_ids: mergedItemIds
        }
        this.addTempDraftOrder(
          siblingSupplierId,
          this.localOriginal.new_supplier_order
        )
      }
      this.newSupplierOrder = false
      this.currentView = 'order'
      this.autoAttachedSiblingName =
        sibling.item?.item_name ?? sibling.parent_item_name ?? null
    },
    toggleComponentExpanded(componentId) {
      if (this.expandedComponentIds.includes(componentId)) {
        this.expandedComponentIds = this.expandedComponentIds.filter(
          (id) => id !== componentId
        )
      } else {
        this.expandedComponentIds = [...this.expandedComponentIds, componentId]
      }
    },
    applyDraftOrdersDecision(orders, { autoCreateIfEmpty = true } = {}) {
      if (!this.localOriginal?.item) return
      if (this.isNested) {
        this.syncOrderSelectionFromParent()
        return
      }
      // Ne jamais réévaluer pendant que l'utilisateur est activement en train
      // de créer/remplir une nouvelle commande : ce watcher est redéclenché
      // par nos propres actions (créer un brouillon, le nettoyer via
      // "Retour", récupérer les commandes en cache) et repartait sinon en
      // arrière (retour à la liste) ou recréait aussitôt un nouveau brouillon
      // juste après son annulation.
      if (this.newSupplierOrder) return
      const relevantOrders = this.localOriginal.item.has_supplier
        ? this.effectiveSupplierOrders
        : Array.isArray(orders)
          ? orders
          : this.supplierOrdersActive
      if (relevantOrders.length > 0) {
        if (!this.original.new_supplier_order?.id) {
          this.newSupplierOrder = false
          this.currentView = 'order'
        }
      } else if (autoCreateIfEmpty && !this.original.new_supplier_order?.id) {
        this.handleButtonNewOrder()
      }
    },
    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0
          const v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        }
      )
    },
    defaultEventIdForNewOrder() {
      // Si une livraison par défaut existe (lieu/date), on force la sélection
      // "Autre" (event_id: null) pour que le lieu/date pré-rempli soit visible
      // immédiatement, au lieu de rester masqué derrière les boutons
      // d'événements. Sinon, si le dossier a des événements, event_id reste
      // undefined (pas de valeur envoyée) pour ne présélectionner aucun
      // bouton d'événement.
      if (this.defaultDelivery?.id) return null
      if (this.localDossier?.events?.length) return undefined
      return null
    },
    maybeAutoOpenNewOrderForDedicatedSupplier() {
      // Ne décide qu'une seule fois (sinon un brouillon serait recréé à
      // chaque mutation ultérieure du cache partagé, ex. un frère qui crée
      // le sien).
      if (this.hasDecidedInitialOrderView) return
      if (
        !this.isAutomaticOrder ||
        !this.localOriginal?.item?.has_supplier ||
        this.localOriginal.new_supplier_order?.id
      ) {
        this.hasDecidedInitialOrderView = true
        return
      }
      // Le back n'embarque plus `supplier_orders` statiquement dans
      // `original[]` (BBSW-9257) : la seule source est désormais le fetch
      // async ensureDraftOrdersFetched. Tant qu'il n'a pas résolu pour ce
      // fournisseur, on ne sait pas encore si des commandes existent déjà —
      // décider maintenant gagnerait systématiquement la course contre lui
      // et créerait un brouillon même quand des commandes réelles existent
      // (elles n'auraient alors plus jamais l'occasion de s'afficher, cf.
      // le garde-fou "ne jamais réécraser newSupplierOrder" plus haut). On
      // retente via le watcher supplierOrdersActive dès que le cache
      // partagé se peuple.
      if (this.loadingSupplierOrders) return
      if (this.effectiveSupplierOrders?.length > 0) {
        // BBSW-5393 : si une commande fournisseur existante a déjà exactement
        // le lieu et la date de livraison par défaut du dossier, on la
        // sélectionne directement plutôt que de laisser l'utilisateur la
        // rechercher dans la liste.
        this.selectMatchingSupplierOrder(this.effectiveSupplierOrders)
      } else {
        this.handleButtonNewOrder()
      }
      this.hasDecidedInitialOrderView = true
    },
    selectMatchingSupplierOrder(candidateOrders) {
      if (
        !this.defaultDelivery?.date_time ||
        !(this.defaultDelivery?.id || this.defaultDelivery?.kind)
      ) {
        return
      }
      const matchingOrder = (candidateOrders ?? []).find(
        (order) =>
          order.delivery_place_kind === this.defaultDelivery.kind &&
          order.delivery_place_id === this.defaultDelivery.id &&
          moment(order.delivery_date_time).isSame(
            moment(this.defaultDelivery.date_time)
          )
      )
      if (matchingOrder) {
        this.handleButtonSelectExistingOrder(matchingOrder.id)
      }
    },
    handleButtonNewOrder() {
      // Ne jamais créer de brouillon de commande sans fournisseur résolu —
      // has_supplier peut être vrai alors que supplier_id n'est pas encore
      // renseigné localement (incohérence de la proposition initiale), ce qui
      // faisait créer une commande "temp-" avec recipient_id: null dès le
      // montage du composant, avant toute action de l'utilisateur (BBSW-9257).
      if (!this.localOriginal.item.supplier_id) return
      this.autoAttachedSiblingName = null
      this.localOriginal.supplier_order_id = null
      const existingItemIds = Array.isArray(
        this.localOriginal.new_supplier_order?.item_ids
      )
        ? this.localOriginal.new_supplier_order.item_ids
        : []
      this.localOriginal.new_supplier_order = {
        ...this.localOriginal.new_supplier_order,
        id: `temp-${this.generateUUID()}`,
        recipient_id: this.localOriginal.item.supplier_id,
        item_ids: [
          ...new Set([...existingItemIds, this.localOriginal.item.item_id])
        ],
        delivery: {
          event_id: this.defaultEventIdForNewOrder(),
          place_id: this.defaultDelivery?.id ?? null,
          place_type: this.defaultDelivery?.kind ?? null,
          place_kind: this.defaultDelivery?.kind ?? null,
          delivery_date_time: this.defaultDelivery?.date_time ?? null
        },
        delivery_date_time: this.defaultDelivery?.date_time
      }
      this.localOriginal.supplier_order_id =
        this.localOriginal.new_supplier_order.id
      this.newSupplierOrder = true
      this.addTempDraftOrder(
        this.localOriginal.item.supplier_id,
        this.localOriginal.new_supplier_order
      )
    },
    handleButtonSelectExistingOrder(supplier_order_id) {
      this.autoAttachedSiblingName = null
      if (this.localOriginal.supplier_order_id === supplier_order_id) {
        this.localOriginal.supplier_order_id = null
        this.localOriginal.delivery_place_id = null
        return
      }
      this.localOriginal.delivery_place_id = null
      this.localOriginal.supplier_order_id = supplier_order_id

      if (!this.localOriginal.item.has_supplier) {
        const chosenOrder = this.supplierOrdersActive.find(
          (order) => order?.id === supplier_order_id
        )
        if (chosenOrder?.recipient_id) {
          this.localOriginal.item.supplier_id = chosenOrder.recipient_id
        }
      }
    },
    syncOrderSelectionFromParent() {
      if (!this.isNested) return
      if (!this.parentSupplierOrderId) return
      const isTempOrder = this.parentSupplierOrderId
        .toString()
        .startsWith('temp-')
      if (isTempOrder) {
        const sharedOrder = (
          Array.isArray(this.supplierOrdersActive)
            ? this.supplierOrdersActive
            : []
        ).find((order) => order.id === this.parentSupplierOrderId)
        this.localOriginal.delivery_place_id = null
        this.localOriginal.supplier_order_id = this.parentSupplierOrderId
        if (!sharedOrder) {
          // Le brouillon partagé n'est pas encore dans le cache (fetch
          // async en cours) : on ne bascule pas encore vers la vue "order"
          // pour éviter d'exposer un `new_supplier_order` vide. Le watcher
          // sur `supplierOrdersActive` relancera cette méthode une fois le
          // cache peuplé.
          return
        }
        this.localOriginal.new_supplier_order = sharedOrder
        this.newSupplierOrder = true
        this.currentView = 'order'
        return
      }
      this.newSupplierOrder = false
      this.localOriginal.delivery_place_id = null
      this.localOriginal.supplier_order_id = this.parentSupplierOrderId
    },
    handleQuitNewOrderView() {
      const abandonedOrderId = this.localOriginal.new_supplier_order?.id
      const abandonedSupplierId =
        this.localOriginal.new_supplier_order?.recipient_id
      if (abandonedOrderId?.startsWith('temp-')) {
        // Les composants qui ont adopté ce brouillon (maybeAdoptSiblingOrder)
        // portent leur propre copie de supplier_order_id/new_supplier_order.id.
        // Sans les détacher ici, le brouillon reste perpétuellement "encore
        // référencé ailleurs", n'est jamais purgé du cache partagé, et un
        // nouveau brouillon fantôme s'accumule à chaque cycle "ajouter une
        // commande" / "retour".
        ;(this.allOriginals ?? []).forEach((entry) => {
          if (entry === this.original) return
          if (entry?.supplier_order_id === abandonedOrderId) {
            entry.supplier_order_id = null
          }
          if (entry?.new_supplier_order?.id === abandonedOrderId) {
            entry.new_supplier_order = {
              ...entry.new_supplier_order,
              id: null
            }
          }
        })
        this.removeTempDraftOrder(abandonedSupplierId, abandonedOrderId)
        if (this.localOriginal.supplier_order_id === abandonedOrderId) {
          this.localOriginal.supplier_order_id = null
        }
      }
      this.newSupplierOrder = false
      this.localOriginal.new_supplier_order = {
        ...this.localOriginal.new_supplier_order,
        recipient_id: this.localOriginal.item.supplier_id,
        id: null,
        delivery: {
          event_id: null,
          place_id: null,
          place_type: null,
          place_kind: null,
          delivery_date_time: null
        }
      }
      this.currentView = 'order'
    },
    handleStockQuantityChange(locationId, value) {
      // Le watcher deep sur `original.stock` ne se redéclenche pas de façon
      // fiable pour les clés ajoutées dynamiquement par
      // ensureStockEntriesForAllLocations() (constaté en debug live : un
      // changement de quantité sur un emplacement laissait `order` figé à
      // son ancienne valeur, sur-comptant le total sélectionné). On
      // recalcule donc explicitement ici plutôt que de compter sur ce
      // watcher.
      this.localOriginal.stock[locationId].quantity = value
      this.calcNumToOrder()
    },
    calcNumToOrder() {
      if (!this.localOriginal?.item) return
      if (!this.localOriginal.item.stockable) {
        this.localOriginal.order = this.localOriginal.item.remaining_quantity
        return
      }

      this.localOriginal.order =
        this.totalNeeded -
        (Object.values(this.localOriginal.stock)
          .map((item) => item.quantity)
          .filter((n) => parseInt(n) === n)
          .reduce((a, b) => a + b, 0) +
          this.localOriginal.item.already_allocate)
    },
    sumStocks(obj) {
      let total = 0

      function deepSum(o) {
        for (const value of Object.values(o)) {
          if (typeof value === 'number') {
            total += value
          } else if (typeof value === 'object' && value !== null) {
            deepSum(value)
          }
        }
      }

      deepSum(obj)
      return total
    },
    checkAndCreateTempOrder() {
      if (!this.newSupplierOrder) return
      const supplierId = this.localOriginal.item.supplier_id
      let placeId = null
      let placeType = null
      let placeKind = null
      let placeName = null
      let event = null
      const delivery = this.localOriginal.new_supplier_order.delivery

      if (delivery.event_id) {
        event = this.localDossier?.events.find(
          (event) => event.id === delivery.event_id
        )
        placeId = event?.place_model?.id
        placeType = event?.place_type
        placeKind = event?.place_kind
      } else {
        placeId = delivery.place_id
        placeType = delivery.place_type
        placeKind = delivery.place_kind
      }

      if (!supplierId) return

      const currentOrder = this.localOriginal.new_supplier_order
      const itemId = this.localOriginal.item.item_id

      if (currentOrder && currentOrder.id?.startsWith('temp-')) {
        const updatedOrder = {
          ...currentOrder,
          id: currentOrder.id,
          recipient_id: supplierId,
          delivery: {
            ...currentOrder.delivery,
            place_id: placeId,
            place_type: placeType,
            place_kind: placeKind,
            place_name: delivery.place_name,
            place_model: {
              place_id: placeId,
              place_type: placeType,
              place_kind: placeKind,
              full_name: delivery.place_name
            },
            event_id: delivery.event_id,
            delivery_date_time: event
              ? event.date_from
              : delivery.delivery_date_time
          },
          delivery_date_time: event
            ? event.date_from
            : delivery.delivery_date_time,
          delivery_place: {
            full_name: event ? event.place_model?.name : delivery.place_name
          },
          item_ids: [...new Set([...currentOrder.item_ids, itemId])]
        }

        const index = this.localNewOrders.findIndex(
          (order) => order.id === currentOrder.id
        )
        if (index !== -1) {
          this.localNewOrders.splice(index, 1, updatedOrder)
        }

        this.localOriginal.new_supplier_order = updatedOrder
        this.addTempDraftOrder(supplierId, updatedOrder)
        return
      }

      const tempOrderId = `temp-${this.generateUUID()}`
      const newOrder = {
        id: tempOrderId,
        recipient_id: supplierId,
        delivery: {
          place_type: placeType,
          place_kind: placeKind,
          place_id: placeId,
          event_id: delivery.event_id,
          place_name: delivery.place_name,
          place_model: {
            place_id: placeId,
            place_type: placeType,
            place_kind: placeKind,
            full_name: delivery.place_name
          },
          delivery_date_time: event
            ? event.date_from
            : delivery.delivery_date_time
        },
        delivery_place: {
          full_name: event ? event.place_model?.name : delivery.place_name
        },
        delivery_date_time: event
          ? event.date_from
          : delivery.delivery_date_time,
        item_ids: [itemId]
      }
      this.localNewOrders.push(newOrder)

      this.localOriginal.new_supplier_order = newOrder
      this.addTempDraftOrder(supplierId, newOrder)
      this.handleButtonSelectExistingOrder(newOrder.id)
    }
  }
}
</script>
